import React, { useEffect } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { useTextPersistence } from "qwanyx";
import { useTextEditor } from './hooks/useTextEditor';
import { useAggregatorDisplay } from './hooks/useAggregatorDisplay';
import { usePositioning } from './hooks/usePositioning';
import { useAggregator } from './hooks/useAggregator';
import { AggregatorSettings } from './AggregatorSettings';

export const Aggregator = ({
  name,
  text,
  fontSize,
  fontFamily = 'Arial',
  fontWeight = 'normal',
  fontStyle = 'normal',
  textAlign = 'left',
  color = '#000000',
  position = 'relative',
  top,
  left,
  right,
  bottom,
  unit = 'px',
  lockedEdges = { top: false, left: false, right: false, bottom: false },
  backgroundColor,
  borderRadius,
  padding,
  cssClass,
  qwantumId,
  onSave,
  style,
  hidden,
  currentTime = false,
  offspringCards = false,
  payable = false,
  receivable = false,
  balance = false,
  stackCount = false,
  payables = false,
  ...props
}) => {
  const { 
    connectors: { connect, drag }, 
    hasSelectedNode,
    isSelected,
    isHovered,
    actions: { setProp },
    parent
  } = useNode((state) => ({
    hasSelectedNode: state.events.selected,
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    parent: state.data.parent
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Persistence hook
  const { text: persistedText } = useTextPersistence(qwantumId, onSave);

  // Context awareness
  const aggregatorContext = useAggregator();

  // Custom hooks
  const {
    editable,
    setEditable,
    textRef,
    handleInput,
    handleBlur
  } = useTextEditor(qwantumId, onSave, text, persistedText);

  const { displayContent, rawText } = useAggregatorDisplay({
    text,
    persistedText,
    qwantumId,
    currentTime,
    offspringCards,
    payable,
    receivable,
    balance,
    stackCount,
    payables,
    context: aggregatorContext // Pass context to display hook
  });

  const { containerStyle, getTextStyle } = usePositioning({
    isSelected,
    isHovered,
    editable,
    position,
    lockedEdges,
    top,
    left,
    right,
    bottom,
    unit,
    backgroundColor,
    borderRadius,
    padding,
    style
  });

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode, setEditable]);

  if (hidden) return null;

  return (
    <div 
      ref={ref => connect(drag(ref))}
      name={name}
      className={cssClass}
      onClick={() => enabled && setEditable(true)}
      style={containerStyle}
      {...props}
    >
      {editable ? (
        <div
          ref={textRef}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onInput={handleInput(setProp)}
          onBlur={handleBlur}
          style={{
            ...getTextStyle({ fontSize, fontFamily, fontWeight, fontStyle, textAlign, color })
          }}
        >
          {rawText}
        </div>
      ) : (
        <div>
          {displayContent.text && (
            <div
              style={{
                ...getTextStyle({ fontSize, fontFamily, fontWeight, fontStyle, textAlign, color })
              }}
            >
              {displayContent.text}
            </div>
          )}
          {displayContent.superpowerRows.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: displayContent.text ? '8px' : '0' }}>
              <tbody>
                {displayContent.superpowerRows.map((row, index) => (
                  <tr key={index}>
                    <td style={{
                      ...getTextStyle({ fontSize, fontFamily, fontWeight, fontStyle, textAlign: 'left', color })
                    }}>
                      {row.label}
                    </td>
                    <td style={{ 
                      textAlign: 'right', 
                      paddingLeft: '8px',
                      ...getTextStyle({ fontSize, fontFamily, fontWeight, fontStyle, textAlign: 'right', color })
                    }}>
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

Aggregator.craft = {
  props: {
    name: "",
    text: "Hi",
    fontSize: 20,
    fontFamily: 'Arial',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#000000',
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    unit: 'px',
    lockedEdges: { top: false, left: false, right: false, bottom: false },
    backgroundColor: '',
    borderRadius: 0,
    padding: 0,
    cssClass: '',
    qwantumId: '',
    currentTime: false,
    offspringCards: false,
    payable: false,
    receivable: false,
    balance: false,
    stackCount: false,
    payables: false
  },
  rules: {
    canDrag: (node) => node.data.props.text !== "Drag"
  },
  related: {
    settings: AggregatorSettings
  }
};